import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity, ArrowRight, Building2, CheckCircle2, ClipboardCheck,
  Compass, Database, Hexagon, HelpCircle, KeyRound, MessageSquare, Rocket,
  ShieldCheck, Sparkles, UserPlus, Workflow, Zap, type LucideIcon,
} from "lucide-react";
import { EmptyState, ErrorBox, PageHeader } from "@/components/common";
import { MarkdownContent } from "@/components/MarkdownContent";
import { rawFetch } from "@/lib/api/client";
import type { ContentPage } from "@/lib/siteContent";

const ICONS: Record<string, LucideIcon> = {
  Activity, Building2, ClipboardCheck, Compass, Database, Hexagon, KeyRound,
  MessageSquare, Rocket, ShieldCheck, UserPlus, Workflow, Zap,
};

function field(metadata: Record<string, unknown>, key: string, fallback = ""): string {
  const value = metadata[key];
  return typeof value === "string" ? value : fallback;
}

function internalHref(value: unknown): string | undefined {
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//")
    ? value
    : undefined;
}

function ContentLink({ href, label, primary = false }: { href?: string; label?: string; primary?: boolean }) {
  if (!href || !label) return null;
  return <a className={primary ? "btn-primary" : "btn-ghost"} href={href}>{label}{primary && <ArrowRight size={15} className="ml-1 inline" />}</a>;
}

export function Journey() {
  const page = useQuery({
    queryKey: ["published-site-content", "journey"],
    queryFn: () => rawFetch<ContentPage>("/site-content/public/pages/journey/", undefined, "json", false),
  });
  const sections = page.data?.sections ?? [];
  const stages = useMemo(
    () => sections.filter((section) => section.metadata.kind === "stage"),
    [sections],
  );
  const faqs = sections.filter((section) => section.metadata.kind === "faq");
  const outcome = sections.find((section) => section.metadata.kind === "outcome");
  const [active, setActive] = useState(3);
  const current = stages.find((section) => section.metadata.n === active) ?? stages[0];
  const metadata = page.data?.metadata ?? {};
  const flow = Array.isArray(metadata.flow) ? metadata.flow.filter((item): item is string => typeof item === "string") : [];

  if (page.isPending) return <div className="card text-sm text-muted" role="status">Loading journey…</div>;
  if (page.isError) return <ErrorBox error={page.error} />;
  if (!page.data) return <EmptyState title="Journey content is unavailable." />;

  const activeIndex = current ? stages.indexOf(current) : -1;
  const stageData = current?.metadata ?? {};
  const stageNumber = typeof stageData.n === "number" ? stageData.n : activeIndex + 1;
  const StageIcon = ICONS[field(stageData, "Icon")] ?? Compass;
  const primary = metadata.primary_cta && typeof metadata.primary_cta === "object"
    ? metadata.primary_cta as Record<string, unknown>
    : {};
  const secondary = metadata.secondary_cta && typeof metadata.secondary_cta === "object"
    ? metadata.secondary_cta as Record<string, unknown>
    : {};
  const outcomePrimary = outcome?.metadata.primary_cta && typeof outcome.metadata.primary_cta === "object"
    ? outcome.metadata.primary_cta as Record<string, unknown>
    : {};
  const outcomeSecondary = outcome?.metadata.secondary_cta && typeof outcome.metadata.secondary_cta === "object"
    ? outcome.metadata.secondary_cta as Record<string, unknown>
    : {};

  return (
    <div className="space-y-10">
      <PageHeader title={page.data.title} subtitle={page.data.subtitle} />

      <header className="text-center">
        <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs text-muted">
          <Sparkles size={13} className="text-accent" /> {field(metadata, "eyebrow", "The Ontoligent journey")}
        </div>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <ContentLink href={internalHref(primary.href)} label={field(primary, "label")} primary />
          <ContentLink href={internalHref(secondary.href)} label={field(secondary, "label")} />
        </div>
      </header>

      {flow.length > 0 && (
        <div className="card overflow-x-auto">
          <div className="flex min-w-max items-center gap-2 text-xs text-muted">
            {flow.map((step, index) => (
              <span key={`${step}-${index}`} className="flex items-center gap-2">
                <span className="rounded-md bg-border/40 px-2 py-1 text-text">{step}</span>
                {index < flow.length - 1 && <ArrowRight size={12} />}
              </span>
            ))}
          </div>
        </div>
      )}

      {stages.length > 0 ? (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
          <ol className="space-y-1">
            {stages.map((stage, index) => {
              const stageMeta = stage.metadata;
              const number = typeof stageMeta.n === "number" ? stageMeta.n : index + 1;
              const Icon = ICONS[field(stageMeta, "Icon")] ?? Compass;
              return (
                <li key={stage.id}>
                  <button
                    type="button"
                    aria-current={number === stageNumber ? "step" : undefined}
                    onClick={() => setActive(number)}
                    className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition ${number === stageNumber ? "border-accent bg-accent/10" : "border-border hover:border-accent/40"}`}
                  >
                    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs ${number === stageNumber ? "bg-accent text-white" : "bg-border/50 text-muted"}`}>{number}</span>
                    <Icon size={15} className={number === stageNumber ? "text-accent" : "text-muted"} />
                    <span className="truncate">{field(stageMeta, "stage", stage.title)}</span>
                    {field(stageMeta, "phase") && <span className="ml-auto rounded bg-border/40 px-1.5 py-0.5 text-[10px] text-muted">{field(stageMeta, "phase")}</span>}
                  </button>
                </li>
              );
            })}
          </ol>

          {current && (
            <div className="card min-w-0">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent/15 text-accent"><StageIcon size={22} /></span>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted">Stage {stageNumber} · {field(stageData, "surface")}</div>
                  <h2 className="text-lg font-semibold">{field(stageData, "stage", current.title)}</h2>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border p-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">You do</div>
                  <p className="text-sm">{field(stageData, "user")}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Ontoligent does</div>
                  <p className="text-sm">{field(stageData, "platform")}</p>
                </div>
              </div>
              <div className="mt-3 rounded-lg border border-accent/30 bg-accent/5 p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-accent"><CheckCircle2 size={13} /> Worked example</div>
                <p className="break-words font-mono text-xs text-text">{field(stageData, "example")}</p>
              </div>
              <div className="mt-4 flex justify-between">
                <button className="btn-ghost text-sm" disabled={activeIndex <= 0} onClick={() => setActive(Number(stages[activeIndex - 1]?.metadata.n))}>← Previous</button>
                <button className="btn-ghost text-sm" disabled={activeIndex < 0 || activeIndex >= stages.length - 1} onClick={() => setActive(Number(stages[activeIndex + 1]?.metadata.n))}>Next →</button>
              </div>
            </div>
          )}
        </section>
      ) : <EmptyState title="No journey stages have been published." />}

      {faqs.length > 0 && (
        <section>
          <h2 className="mb-1 flex items-center gap-2 text-xl font-semibold"><HelpCircle size={20} className="text-accent" /> Decision doubts, unlocked</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {faqs.map((faq) => (
              <div key={faq.id} className="card card-interactive">
                <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold"><HelpCircle size={15} className="shrink-0 text-accent" />{faq.title}</div>
                <MarkdownContent markdown={faq.content_markdown} />
              </div>
            ))}
          </div>
        </section>
      )}

      {outcome && (
        <section className="card text-center">
          <h2 className="text-lg font-semibold">{outcome.title}</h2>
          <MarkdownContent markdown={outcome.content_markdown} className="mx-auto mt-2 max-w-2xl text-muted" />
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <ContentLink href={internalHref(outcomePrimary.href)} label={field(outcomePrimary, "label")} primary />
            <ContentLink href={internalHref(outcomeSecondary.href)} label={field(outcomeSecondary, "label")} />
          </div>
        </section>
      )}
    </div>
  );
}
